import moment from "moment-jalaali"
import "moment/locale/fa"
import { p2e } from "@/app/utils/replaceNumber"

// Latin digits everywhere (CF-2912); only the calendar and month names are Persian.
moment.loadPersian({ usePersianDigits: false })
moment.locale("fa")

const JALALI_FORMATS = ["jYYYY/jMM/jDD", "jYYYY/jM/jD", "jYYYY-jMM-jDD", "jYYYY-jM-jD"]

// Month/weekday labels are kept here instead of being read from moment's global
// locale data: `moment/locale/fa` is imported from several chunks and can be
// evaluated after `loadPersian()`, which wipes the Persian `jMonths` and leaves
// moment falling back to its latinized names ("Shahrivar" instead of "شهریور").
export const JALALI_MONTHS = [
	"فروردین",
	"اردیبهشت",
	"خرداد",
	"تیر",
	"مرداد",
	"شهریور",
	"مهر",
	"آبان",
	"آذر",
	"دی",
	"بهمن",
	"اسفند",
]

// Already ordered from the first day of the Jalali week (Saturday).
export const JALALI_WEEKDAYS_MIN = ["ش", "ی", "د", "س", "چ", "پ", "ج"]

// Saturday, as a moment day index (0 = Sunday).
export const JALALI_FIRST_DAY_OF_WEEK = 6

export function normalizeJalaliDateInput(value?: string) {
	return p2e(value || "").trim()
}

export function parseJalaliDate(value?: string) {
	const normalized = normalizeJalaliDateInput(value)
	if (!normalized) return null

	// moment-jalaali's multi-format parser returns `undefined` (not an invalid
	// moment) when no format in JALALI_FORMATS matches — see
	// makeDateFromStringAndArray in moment-jalaali. Its @types lie about this.
	const parsed = moment(normalized, JALALI_FORMATS, true) as
		| ReturnType<typeof moment>
		| undefined
	return parsed?.isValid() ? parsed : null
}

export function isValidJalaliDate(value?: string) {
	return Boolean(parseJalaliDate(value))
}

// Formats without going through moment's locale postformat, so the result is
// always `jYYYY/jMM/jDD` with latin digits regardless of the active locale.
export function formatJalaliParts(year: number, monthIndex: number, day: number) {
	const pad = (part: number) => String(part).padStart(2, "0")
	return `${year}/${pad(monthIndex + 1)}/${pad(day)}`
}

export function formatJalaliDate(date: any) {
	return formatJalaliParts(date.jYear(), date.jMonth(), date.jDate())
}

export function jalaliToGregorianDate(value?: string) {
	const parsed = parseJalaliDate(value)
	return parsed ? p2e(parsed.format("YYYY-MM-DD")) : ""
}

export function gregorianToJalaliDate(value?: string) {
	if (!value) return ""
	const parsed = moment(value, "YYYY-MM-DD", true)
	return parsed.isValid() ? parsed.format("jYYYY/jMM/jDD") : ""
}

export function getJalaliDateYearsAgo(years: number) {
	return formatJalaliDate(moment().subtract(years, "years"))
}
